import { observable, action, reaction, IReactionDisposer, runInAction } from 'mobx';
import {
    StreemAPI,
    APITypes,
    StreemApiUpdateUserAvailabilityRequestAvailabilityStatus,
    StreemApiUserAvailabilityAvailabilityStatus,
} from '@streem/api';
import { Logger } from '@streem/logger';
import { AuthStore } from './auth_store';
import appLogger from '../util/logging/app_logger';
import { CompanySettingsStore } from './company_settings_store';
import { SDKStore } from './sdk_store';

export class UserStore {
    private unsubscribers: IReactionDisposer[] | undefined = [];
    private log: Logger;

    public constructor(
        private readonly authStore: AuthStore,
        private readonly sdkStore: SDKStore,
        private readonly companySettingsStore?: CompanySettingsStore,
    ) {
        this.log = appLogger.extend('user-store');
    }

    // Omit sid and companySid from the User type as these details are
    // most reliably source from AuthStore.
    @observable
    public user: Omit<APITypes.StreemApiUser, 'sid' | 'companySid'> | undefined = undefined;

    @observable
    public initialized = false;

    @observable
    public availabilityStatus: StreemApiUserAvailabilityAvailabilityStatus;

    // NOTE: Used as a MobX-observable trigger rather than a boolean flag so the reaction
    // fires on every answer event, not just the first. A boolean toggle would only fire once.
    @observable
    public callsAnswered = 0;

    @action.bound
    public setUser(user: APITypes.StreemApiUser | undefined): void {
        this.user = user;
    }

    @action.bound
    public async updateUserMeta(newUserMeta: APITypes.StreemApiUpdateUserRequest): Promise<void> {
        if (!this.authStore.userId) {
            throw new Error('Unable to update user meta without authenticated user.');
        }

        const response = await StreemAPI.users.updateUser(this.authStore.userId, newUserMeta);
        runInAction(() => {
            this.setUser(response.user);
        });
    }

    @action.bound
    public onCallAnswered(): void {
        this.callsAnswered++;
    }

    public connect(): void {
        this.unsubscribers.push(
            reaction(
                () => [this.authStore.userId, this.authStore.companyCode],
                ([id, companyCode]) => {
                    if (id && companyCode) {
                        this.log.info(
                            `User store connecting with userId=${id} and companyCode=${companyCode}`,
                        );
                        StreemAPI.users.getUser(id).then(response => {
                            action(() => {
                                this.initialized = true;
                                this.setUser(response.user);
                            })();
                        });
                    } else {
                        this.log.warn(
                            `Setting user to undefined, id or company code are missing, userId=${id}, companyCode=${companyCode}`,
                        );
                        this.initialized = true;
                        this.setUser(undefined);
                    }
                },
                // By default this is false. Prior to ST-7124 it was set to true.
                // Setting this to true doesn't seem necessary. The effect runs when `this.authStore.userId`
                // or `this.authStore.companyCode` change. Triggering it immediately causes the else block
                // to set `user` to `undefined`, but the initial state of `user` is already `undefined` (line 18).
                // The effect will run again once `authStore` initializes a user, at which point `userId`
                // and `companyCode` are retrieved from `authStore.user`.
                { fireImmediately: false },
            ),

            // When the app starts, ensure the user's availability status is set to Not Ready
            // if the user is not Reserved or In Call. This reaction waits until both expert
            // availability is confirmed for the company and the user's wall sync connection
            // is established before making the API call.
            reaction(
                () => [
                    this.companySettingsStore.expertAvailabilityEnabled,
                    this.sdkStore.userWallSyncConnected.current,
                ],
                ([expertAvailabilityEnabled, userWallSyncConnected]) => {
                    if (expertAvailabilityEnabled && userWallSyncConnected) {
                        this.updateUserAvailabilityStatusViaApi(this.authStore.userId).catch(e => {
                            this.log.warn('Failed to set user availability on app start', e);
                        });
                    }
                },
            ),
        );
    }

    @action.bound
    public async setAvailabilityStatus(
        availabilityStatus: StreemApiUserAvailabilityAvailabilityStatus,
    ): Promise<void> {
        if (availabilityStatus === StreemApiUserAvailabilityAvailabilityStatus.OFFLINE) {
            const getUserAvailabilityResponse = await StreemAPI.users.getUserAvailability(
                this.authStore.userId,
            );
            runInAction(() => {
                this.availabilityStatus =
                    getUserAvailabilityResponse.userAvailability.availabilityStatus;
            });
            return;
        }

        this.availabilityStatus = availabilityStatus;
    }

    public async updateUserAvailabilityStatusViaApi(
        userId: string,
        newAvailabilityStatus?: StreemApiUpdateUserAvailabilityRequestAvailabilityStatus,
    ): Promise<void> {
        const updateUserAvailabilityResponse = await StreemAPI.users.updateUserAvailability(
            userId,
            {
                availabilityStatus:
                    newAvailabilityStatus ??
                    StreemApiUpdateUserAvailabilityRequestAvailabilityStatus.NOTAVAILABLE,
            },
        );

        runInAction(() => {
            this.log.info(
                `User availability set to: ${updateUserAvailabilityResponse.userAvailability.availabilityStatus}`,
            );
            this.setAvailabilityStatus(
                updateUserAvailabilityResponse.userAvailability.availabilityStatus,
            );
        });
    }

    public disconnect(): void {
        if (this.unsubscribers) {
            this.initialized = false;
            this.unsubscribers.forEach(disposer => disposer());
        }
    }
}
